/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useCallback } from 'react';
import { useMutation } from '@apollo/react-hooks';

import type { FlagsStateContainer } from '@confluence/flags';

import type {
	useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation as useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation$data,
	useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutationVariables as useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation$variables,
} from './__types__/useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation';
import type {
	useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation as useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation$data,
	useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutationVariables as useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation$variables,
} from './__types__/useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation';
import { useBulkRoleFlags } from './useBulkRoleFlags';

const useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation = gql`
	mutation useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation(
		$principalId: ID!
		$principalType: RoleAssignmentPrincipalType!
		$roleId: ID!
		$spaceTypes: [BulkRoleAssignmentSpaceType]!
	) {
		bulkSetRoleAssignmentToSpaces(
			input: {
				roleAssignment: {
					principal: { principalId: $principalId, principalType: $principalType }
					roleId: $roleId
				}
				spaceTypes: $spaceTypes
			}
		) {
			taskId
			success
			errors {
				message
			}
		}
	}
`;
const useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation = gql`
	mutation useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation(
		$principalId: ID!
		$principalType: RoleAssignmentPrincipalType!
		$spaceTypes: [BulkRoleAssignmentSpaceType]!
	) {
		bulkRemoveRoleAssignmentFromSpaces(
			input: {
				principal: { principalId: $principalId, principalType: $principalType }
				spaceTypes: $spaceTypes
			}
		) {
			taskId
			success
			errors {
				message
			}
		}
	}
`;
export const useBulkRoleSetAssignment = ({
	closeDialog,
	flags,
}: {
	closeDialog: () => void;
	flags: FlagsStateContainer;
}) => {
	const { showErrorFlag, showProcessingFlag } = useBulkRoleFlags({ flags });

	const onMutationErrored = useCallback(() => {
		closeDialog();
		void showErrorFlag();
	}, [closeDialog, showErrorFlag]);

	const onAsyncMutationSubmitted = useCallback(
		(data) => {
			closeDialog();
			/**
			 * The backend is counting the mutation as a success for some exceptions
			 * but returning an error in the mutation response.  We need to check for
			 * these here.
			 */
			if (
				data?.bulkSetRoleAssignmentToSpaces?.errors?.length > 0 ||
				data?.bulkRemoveRoleAssignmentFromSpaces?.errors?.length > 0
			) {
				onMutationErrored();
			} else {
				if (
					data.bulkSetRoleAssignmentToSpaces?.taskId ||
					data.bulkRemoveRoleAssignmentFromSpaces?.taskId
				) {
					// show the progress flag
					void showProcessingFlag();
				}
			}
		},
		[closeDialog, showProcessingFlag, onMutationErrored],
	);

	const [bulkSetRoleAssignmentToSpaces, { data: bulkAssignmentData }] = useMutation<
		useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation$data,
		useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation$variables
	>(useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation, {
		onCompleted: onAsyncMutationSubmitted,
		onError: () => {
			closeDialog();
			onMutationErrored();
		},
	});
	const [bulkRemoveRoleAssignment, { data: bulkRemoveAssignmentData }] = useMutation<
		useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation$data,
		useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation$variables
	>(useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation, {
		onCompleted: onAsyncMutationSubmitted,
		onError: () => {
			closeDialog();
			onMutationErrored();
		},
	});
	return {
		bulkSetRoleAssignmentToSpaces,
		bulkRemoveRoleAssignment,
		bulkAssignmentData,
		bulkRemoveAssignmentData,
	};
};
